import React, { useState, useRef, useEffect } from 'react';
import { formatDate, isAfterNow, addHours } from '../../../shared/utils/dateUtils';
import { useQuizOverview } from '../../../shared/hooks/useQuizOverview';
import { QuizOverviewModalProps } from '../../../interfaces/screens/content/quizOverview';
/**
 * QuizOverviewModal displays quiz details in a modal and allows users to start the quiz
 * or shows restriction messages if the quiz cannot be taken.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.quizId - ID of the quiz to display
 * @param {boolean} props.finalQuiz - Whether this is a final quiz
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onStartQuiz - Function to call when starting the quiz
 * @param {Function} props.onAssessmentComplete - Function to call when assessment is completed
 */
const QuizOverviewModal = ({
  quizId,
  finalQuiz = false,
  onClose,
  onStartQuiz,
  onAssessmentComplete,
}: QuizOverviewModalProps) => {
  const { quizOverview, loading, error } = useQuizOverview(quizId, finalQuiz);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(true);
  const [contentLoaded, setContentLoaded] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle entrance animation on component mount
  useEffect(() => {
    setIsOpening(true);
    const timer = setTimeout(() => {
      setIsOpening(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (error) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Handle content load transition
  useEffect(() => {
    if (!loading && !quizOverview?.id) {
      handleClose();
    }
    if (!loading && quizOverview) {
      const timer = setTimeout(() => {
        setContentLoaded(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setContentLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, quizOverview]);

  // Handle close with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Handle outside click to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle quiz start
  const handleStartQuiz = () => {
    onStartQuiz({
      id: quizOverview?.id || 0,
      sectionId: quizId,
      questions: quizOverview?.questions || [],
      onAssessmentComplete: onAssessmentComplete || undefined,
    });
  };

  // Check quiz availability
  const lastFinishDate = quizOverview?.last_finish_date;
  const questions = quizOverview?.questions || [];

  let restrictionMessage = '';

  if (lastFinishDate && questions.length === 0) {
    const nextAvailableDate = addHours(lastFinishDate, 24);
    const isAvailable = isAfterNow(nextAvailableDate);

    if (!isAvailable) {
      restrictionMessage = `You can only attempt this quiz once every 24 hours. The next attempt will be available on ${formatDate(
        nextAvailableDate,
      )}.`;
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 w-full flex items-center justify-center bg-black transition-opacity duration-300 ${
        isClosing ? 'bg-opacity-0' : isOpening ? 'bg-opacity-0' : 'bg-opacity-50'
      }`}
      style={{ backdropFilter: isOpening ? 'none' : 'blur(5px)' }}
    >
      <div
        ref={modalRef}
        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300 ${
          isClosing
            ? 'opacity-0 scale-95 translate-y-4'
            : isOpening
              ? 'opacity-0 scale-95 translate-y-4'
              : 'opacity-100 scale-100 translate-y-0'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Modal header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-gray-700">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-800 dark:text-white">
            {finalQuiz ? 'Final Assessment' : 'Quiz Overview'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-600 dark:text-gray-300"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal content */}
        <div className="flex-1 overflow-auto relative">
          {/* Skeleton loader */}
          {loading && (
            <div className="p-6 space-y-4">
              <div className="animate-pulse">
                {/* Title skeleton */}
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 mb-6"></div>

                {/* Description skeleton - multiple lines */}
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-5/6"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-4/6"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-5/6"></div>
                </div>
              </div>
            </div>
          )}

          {/* Actual content */}
          {!loading && quizOverview && (
            <div
              className={`transition-all duration-500 ease-out p-6 ${
                contentLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{quizOverview?.name || '-'}</h3>

              <div
                className="prose dark:prose-invert prose-headings:font-semibold prose-a:text-primary max-w-none mb-8 dark:text-white"
                dangerouslySetInnerHTML={{ __html: quizOverview?.description || '' }}
              />

              {quizOverview?.questions?.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-8">
                  <div className="flex items-center text-blue-800 dark:text-blue-300 mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-semibold">Quiz Details</span>
                  </div>
                  <p className="text-blue-700 dark:text-blue-200 text-sm">
                    This quiz contains {quizOverview.questions.length} question
                    {quizOverview.questions.length !== 1 ? 's' : ''}.
                    {finalQuiz && ' This is a final assessment for this section.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="p-5 border-t border-gray-100 dark:border-gray-700 flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
          >
            Cancel
          </button>

          {restrictionMessage ? (
            <div className="px-4 py-3 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 rounded-lg text-sm max-w-md">
              <div className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <p>{restrictionMessage}</p>
              </div>
            </div>
          ) : (
            <button
              disabled={loading || questions.length === 0}
              onClick={handleStartQuiz}
              className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Start Quiz
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizOverviewModal;
